import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cdFetch, cdPost } from "../api";
import { queryKeys } from "../queryKeys";
import {
  IMarkAsReadResponse,
  INotificationResponse,
} from "../../pages/notifications/NotificationsConstants";
import { emit } from "process";

const countAllNotifications = (notificationsData: any) => {
  if (!notificationsData || typeof notificationsData !== 'object') {
    return 0;
  }

  let totalCount = 0;

  // Iterate through all categories in the response
  Object.keys(notificationsData).forEach((category) => {
    const categoryData = notificationsData[category];
    if (Array.isArray(categoryData)) {
      totalCount += categoryData.length;
    }
  });

  return totalCount;
};

export const useNotificationsData = (
  role: string,
  commodity: string | null,
  userEmail: string
) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: [queryKeys.notifications, role, commodity],
    queryFn: () => getNotificationsData(role, commodity, userEmail),
    enabled: !!role,
    refetchInterval: 20000, // poll every 20 seconds
    refetchOnMount: true, // Forces refetch every time the component mounts
    staleTime: 0, // Disable stale data
  });
  const notificationsData = { ...data?.data };
  return { notificationsData, isLoading, refetch };
};

const getNotificationsData = (
  role: string,
  commodity: string | null,
  userEmail: string
) => {
  let qParams = "";
  if (role) {
    qParams += `role=${JSON.stringify(role)}&`;
  }
  if (userEmail) {
    qParams += `email=${JSON.stringify(userEmail)}&`;
  }
  if (role !== "Lead") {
    qParams += `commodity=${commodity ? JSON.stringify(commodity) : null}&`;
  }
  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }
  const res = cdFetch(`/getNotifications${qParams}`);
  return res;
};

export const useNotificationsCount = (
  role: string,
  commodity: string[] | null,
  email: string
) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: [queryKeys.notificaitonsCount],
    // queryFn: () => getNotificationsCount(role, commodity, email),
    queryFn: () => getAllNotificationsData(role, commodity, email), // temporary changes
    enabled: !!(role && commodity && commodity.length && email),
    refetchInterval: 20000, // poll every 20 seconds
  });
  // const notificationsCount = data?.data?.data;
  const notificationsData = { ...data?.data }; // temporary changes
  const notificationsCount = countAllNotifications(notificationsData.data) // temporary changes
  return { notificationsCount, refetchCount: refetch };
};

export const useNotificationsPageCount = (
  role: string,
  commodity: string[] | null,
  email: string
) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: [queryKeys.notificaitonsCount, role, commodity, email],
    // queryFn: () => getNotificationsCount(role, commodity, email),
    queryFn: () => getAllNotificationsData(role, commodity, email), // temporary changes
    enabled: !!(role && commodity && commodity.length && email),
    refetchInterval: 20000, // poll every 20 seconds
    refetchOnMount: true, // Forces refetch every time the component mounts
    staleTime: 0, // Disable stale data
  });
  // const notificationsCount = data?.data?.data;
  const notificationsData = { ...data?.data }; // temporary changes
  const notificationsCount = countAllNotifications(notificationsData.data) // temporary changes
  return { notificationsCount, refetchCount: refetch };
};

const getNotificationsCount = (
  role: string,
  commodity: string[] | null,
  email: string
) => {
  let qParams = "";
  if (role) {
    qParams += `role=${JSON.stringify(role)}&`;
  }
  if (email) {
    qParams += `email=${JSON.stringify(email)}&`;
  }
  if (commodity && commodity.length !== 0) {
    qParams += `commodity=${JSON.stringify(commodity)}&`;
  }
  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }
  const res = cdFetch(`/getNotificationCount${qParams}`);
  return res;
};

export const useMarkNotificationRead = (
  refetch: () => void,
  refetchCount: () => void
) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: ["notificationRead"],
    mutationFn: (payload: IMarkAsReadResponse) => postNotificationRead(payload),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ["notificationRead"] });
      refetch();
      refetchCount();
    },
  });

  return {
    mutate: mutation.mutate,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
  };
};

const postNotificationRead = (payload: IMarkAsReadResponse) => {
  let res;
  // if (payload?.isInfo) {
  //   res = cdPost(
  //     `/markAsRead?notification_id=${payload?.id}&isInfo=${payload.isInfo}`,
  //     null
  //   );
  // } else {
  //   res = cdPost(`/markAsRead?notification_id=${[payload?.id]}`, null);
  // }
  res = cdPost(`/markAsRead`, payload);
  return res;
};

export const useMarkAllNotificationRead = (
  refetch: () => void,
  refetchCount: () => void
) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: ["allnotificationRead"],
    mutationFn: (userEmail: string) => postAllNotificationRead(userEmail),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ["allnotificationRead"] });
      refetch();
      refetchCount();
    },
  });
  return {
    mutate: mutation.mutate,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
  };
};

const postAllNotificationRead = (userEmail: string) => {
  let res = cdPost(`/markAllAsRead`, { userName: userEmail });
  return res;
};

export const useAllNotificationsData = (
  role: string,
  commodity: string[] | null,
  userEmail: string
) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: [queryKeys.notificationAll, role, commodity, userEmail],
    queryFn: () => getAllNotificationsData(role, commodity, userEmail),
    enabled: !!(role && commodity && commodity?.length && userEmail),
    refetchInterval: 20000, // poll every 20 seconds
    refetchOnMount: true, // Forces refetch every time the component mounts
    staleTime: 0, // Disable stale data
  });
  const notificationsData = { ...data?.data };
  return { notificationsData, isLoading, refetch };
};

const getAllNotificationsData = (
  role: string,
  commodity: string[] | null,

  userEmail: string
) => {
  let qParams = "";
  if (role) {
    qParams += `role=${JSON.stringify(role)}&`;
  }
  if (userEmail) {
    qParams += `email=${JSON.stringify(userEmail)}&`;
  }
  // if (role !== "Lead") {
  //   qParams += `commodity=${commodity ? JSON.stringify(commodity) : null}&`;
  // }
  if (commodity && commodity.length !== 0) {
    qParams += `commodity=${JSON.stringify(commodity)}&`;
  }
  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }
  const res = cdFetch(`/getAllNotifications${qParams}`);
  return res;
};

export const usenotificationFilters = (role: string, userEmail: string) => {
  const { data, isLoading } = useQuery({
    queryKey: [queryKeys.notificationFiltre, role, userEmail],
    queryFn: () => getnotificationFilters(role, userEmail),
    refetchOnMount: true,
    staleTime: 0,
    enabled: !!(role && userEmail),
  });

  let notificationFilters = data?.data?.data;
  let filtersLoaded = isLoading;
  return { notificationFilters, filtersLoaded };
};

const getnotificationFilters = (role: string, userEmail: string) => {
  let qParams = "";

  if (role && role.length !== 0) {
    qParams += `role=${JSON.stringify(role)}&`;
  }
  if (userEmail && userEmail.length !== 0) {
    qParams += `userEmail=${JSON.stringify(userEmail)}&`;
  }
  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }

  const res = cdFetch(`/getNotificationFilters${qParams}`);
  return res;
};
